/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { bookAppointment, getDoctorSlots } from '@/lib/telemedicine';
import { getFamilyDashboard } from '@/lib/pricing';
import AITriageChatbot from '@/components/telemedicine/AITriageChatbot';
import StepSlotPicker, { isoDate } from './booking/StepSlotPicker';
import StepTriage from './booking/StepTriage';
import StepReview from './booking/StepReview';
import FamilyMemberPicker from './booking/FamilyMemberPicker';
import { DoctorHero } from './booking/DoctorHero';
import { WizardStepper, daysAhead } from './booking/WizardStepper';

/**
 * BookingWizard — 3-step booking flow (Slot → Triage → Review).
 *
 * Refactor note: ogni step, l'intestazione medico (DoctorHero) e lo stepper vivono in
 * `components/telemedicine/booking/*`; questo file è solo l'orchestratore di stato.
 */

const BookingWizard = ({ doctor, limits, onClose, onBooked }) => {
  const { t } = useTranslation();

  const [step, setStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => daysAhead(1)[0]);
  const [selectedSlot, setSelectedSlot] = useState(null); // ISO string
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [urgency, setUrgency] = useState('routine');
  const [reason, setReason] = useState('');
  const [triage, setTriage] = useState({
    symptoms: '',
    duration: '',
    severity: 5,
    allergies: '',
    current_meds: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // AI Triage integration
  const [aiTriageOpen, setAiTriageOpen] = useState(false);
  const [aiTriageFinal, setAiTriageFinal] = useState(null);
  const [, setAiTriageSessionId] = useState(null);

  // Smart Booking Family (Sprint 10) — load bundle dashboard once
  const [familyData, setFamilyData] = useState(null);
  const [familyMemberKey, setFamilyMemberKey] = useState(null); // null == "self"

  // Reset when the target doctor changes
  useEffect(() => {
    if (!doctor) return;
    setStep(0);
    setSelectedDate(daysAhead(1)[0]);
    setSelectedSlot(null);
    setUrgency('routine');
    setReason('');
    setFamilyMemberKey(null);
    setTriage({ symptoms: '', duration: '', severity: 5, allergies: '', current_meds: '' });
  }, [doctor]);

  // Load family dashboard once per wizard open. Silent fail — if the patient
  // has no family bundle the picker simply won't render.
  useEffect(() => {
    if (!doctor) return undefined;
    let cancelled = false;
    getFamilyDashboard()
      .then((d) => { if (!cancelled) setFamilyData(d); })
      .catch(() => { if (!cancelled) setFamilyData(null); });
    return () => { cancelled = true; };
  }, [doctor]);

  // Load available slots for the selected day
  useEffect(() => {
    if (!doctor) return undefined;
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedSlot(null);
    getDoctorSlots(doctor.id, isoDate(selectedDate))
      .then((res) => {
        if (!cancelled) setSlots(res.slots || []);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doctor, selectedDate]);

  const isFree = !!limits && limits.remaining > 0;
  const canContinue1 = !!selectedSlot;
  const canContinue2 = useMemo(
    () => !!triage.symptoms && triage.symptoms.trim().length >= 3,
    [triage.symptoms],
  );

  if (!doctor) return null;

  const submit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    try {
      const res = await bookAppointment({
        doctor_id: doctor.id,
        scheduled_at: new Date(selectedSlot).toISOString(),
        reason: reason || undefined,
        urgency,
        triage: {
          symptoms: triage.symptoms || undefined,
          duration: triage.duration || undefined,
          severity: Number(triage.severity) || undefined,
          allergies: triage.allergies || undefined,
          current_meds: triage.current_meds || undefined,
        },
        family_member_key: familyMemberKey || undefined,
        origin_url: window.location.origin,
      });
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      toast.success(t('telemedBookingConfirmed'));
      onBooked?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmLabel = () => {
    if (submitting) return t('processing');
    return isFree ? t('telemedConfirmFreeBooking') : t('telemedProceedToPayment');
  };

  return (
    <Dialog open={!!doctor} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="booking-wizard">
        <DoctorHero doctor={doctor} isFree={isFree} />
        <WizardStepper step={step} />

        {/* Step content */}
        <div className="p-5 max-h-[52vh] overflow-y-auto scrollbar-thin">
          {step === 0 && (
            <StepSlotPicker
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              selectedSlot={selectedSlot}
              onSelectSlot={setSelectedSlot}
              slots={slots}
              loadingSlots={loadingSlots}
              urgency={urgency}
              onUrgencyChange={setUrgency}
            />
          )}
          {step === 1 && (
            <div className="space-y-5">
              <FamilyMemberPicker
                familyData={familyData}
                selectedKey={familyMemberKey}
                onSelect={setFamilyMemberKey}
              />
              <StepTriage
                triage={triage}
                onTriageChange={setTriage}
                reason={reason}
                onReasonChange={setReason}
                aiTriageFinal={aiTriageFinal}
                onOpenAiTriage={() => setAiTriageOpen(true)}
              />
            </div>
          )}
          {step === 2 && (
            <StepReview
              doctor={doctor}
              selectedSlot={selectedSlot}
              urgency={urgency}
              isFree={isFree}
              familyData={familyData}
              familyMemberKey={familyMemberKey}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center gap-2 bg-card">
          {step > 0 ? (
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              data-testid="booking-back-btn"
            >
              <ChevronLeft className="h-4 w-4" /> {t('back')}
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose} data-testid="booking-cancel-btn">
              {t('cancel')}
            </Button>
          )}
          <div className="ml-auto">
            {step < 2 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={(step === 0 && !canContinue1) || (step === 1 && !canContinue2)}
                data-testid="booking-next-btn"
              >
                {t('next')} <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting}
                size="lg"
                data-testid="booking-confirm-btn"
              >
                {confirmLabel()}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* AI Triage Chatbot dialog (mounted separately to overlay on top of the wizard) */}
      <AITriageChatbot
        open={aiTriageOpen}
        onOpenChange={setAiTriageOpen}
        initialContext={{ reason: reason || triage.symptoms || undefined }}
        onSessionCreated={(sid) => setAiTriageSessionId(sid)}
        onFinalized={(final) => {
          setAiTriageFinal(final);
          setTriage((prev) => ({
            ...prev,
            symptoms:
              prev.symptoms && prev.symptoms.trim().length >= 3
                ? prev.symptoms
                : (final?.summary || prev.symptoms || ''),
          }));
          if (!reason && final?.summary) setReason(final.summary.slice(0, 480));
          if (final?.urgency === 'high') setUrgency('urgent');
          else if (final?.urgency === 'medium' && urgency === 'routine') setUrgency('urgent');
        }}
      />
    </Dialog>
  );
};

export default BookingWizard;
